// Field validators for Phase 2 (spec §5, §6, §8). Pure functions so the form,
// the review screen and the jest suite all share them.

export const ALIAS_PATTERN = /^[A-Za-z0-9. ]{2,40}$/;
export const UPI_ID_PATTERN = /^[a-zA-Z0-9._-]{2,256}@[a-zA-Z]{2,64}$/;
/** §8.1 direct-pay UPI ID: the domain part also allows digits, dots and hyphens. */
export const UPI_ID_DIRECT_PATTERN = /^[a-zA-Z0-9._-]{2,256}@[a-zA-Z][a-zA-Z0-9.-]{1,63}$/;
export const MOBILE_PATTERN = /^[6-9]\d{9}$/;
export const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const SWIFT_PATTERN = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
export const INTL_ACCOUNT_PATTERN = /^[A-Za-z0-9]{4,34}$/;
export const IBAN_SHAPE = /^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/;

export const isValidAlias = (alias: string): boolean => ALIAS_PATTERN.test(alias.trim());
export const isValidUpiId = (upiId: string): boolean => UPI_ID_PATTERN.test(upiId.trim());
export const isValidDirectUpiId = (upiId: string): boolean =>
  UPI_ID_DIRECT_PATTERN.test(upiId.trim());
export const isValidMobile = (mobile: string): boolean => MOBILE_PATTERN.test(mobile.trim());
export const isValidIfsc = (ifsc: string): boolean => IFSC_PATTERN.test(ifsc.trim().toUpperCase());
export const isValidSwift = (swift: string): boolean => SWIFT_PATTERN.test(swift.trim().toUpperCase());
export const isValidIntlAccount = (value: string): boolean => INTL_ACCOUNT_PATTERN.test(value.trim());

/** Local transfer account number: digits only, 9–18 (§6.2). */
export const isValidLocalAccountNumber = (value: string): boolean => /^\d{9,18}$/.test(value.trim());

/** ISO 13616 mod-97 check. Input must already be normalised (no spaces, caps). */
export const isValidIban = (iban: string): boolean => {
  const normalised = iban.replace(/\s/g, '').toUpperCase();
  if (!IBAN_SHAPE.test(normalised)) return false;
  const rearranged = normalised.slice(4) + normalised.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const code = /[A-Z]/.test(ch) ? String(ch.charCodeAt(0) - 55) : ch;
    for (const digit of code) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }
  }
  return remainder === 1;
};

/** Normalises a direct-pay mobile: strips spaces, "+91" and a leading 0 (§8.1). */
export const normaliseMobile = (raw: string): string => {
  let value = raw.replace(/\s/g, '');
  if (value.startsWith('+91')) value = value.slice(3);
  else if (value.startsWith('0')) value = value.slice(1);
  return value;
};

/** Parses a standard UPI QR payload: upi://pay?pa=…&pn=…&am=…&cu=INR (§8.1). */
export interface ParsedUpiQr {
  pa: string;
  pn?: string;
  am?: number;
}

export const parseUpiQr = (raw: string): ParsedUpiQr | null => {
  const text = raw.trim();
  if (!text.toLowerCase().startsWith('upi://pay')) return null;
  const queryIndex = text.indexOf('?');
  if (queryIndex < 0) return null;
  const params = new URLSearchParams(text.slice(queryIndex + 1));
  const pa = params.get('pa');
  if (!pa) return null;
  const pn = params.get('pn') ?? undefined;
  const amRaw = params.get('am');
  const am = amRaw ? Number(amRaw) : undefined;
  return {
    pa,
    pn: pn ? decodeURIComponent(pn) : undefined,
    am: am !== undefined && Number.isFinite(am) && am > 0 ? am : undefined,
  };
};

/** Amount field: up to 9 digits and 2 decimals, refused as typed (§7.2). */
export const AMOUNT_PATTERN = /^\d{1,9}(\.\d{0,2})?$/;
export const filterAmountInput = (raw: string, previous: string): string =>
  raw === '' || AMOUNT_PATTERN.test(raw) ? raw : previous;

export const isEasyPin = (pin: string): boolean => {
  if (!/^(\d)\d+$/.test(pin)) return false;
  const repeated = /^(\d)\1+$/.test(pin);
  const ascending = pin.split('').every((digit, index) => index === 0 || Number(digit) === Number(pin[index - 1]) + 1);
  const descending = pin.split('').every((digit, index) => index === 0 || Number(digit) === Number(pin[index - 1]) - 1);
  return repeated || ascending || descending;
};

/** The live server takes a four-digit MPIN only. */
export const MPIN_LENGTH = 4;

/** Client checks for a new MPIN. Returns '' when it may be used. */
export const newMpinError = (mpin: string): string => {
  if (mpin.length !== MPIN_LENGTH || !/^\d+$/.test(mpin)) return `The MPIN is ${MPIN_LENGTH} digits.`;
  if (/^(\d)\1+$/.test(mpin)) return 'The MPIN cannot be the same digit repeated.';
  if (isEasyPin(mpin)) return 'That MPIN is too easy to guess.';
  return '';
};

/** Returns '' when the confirmation matches the new MPIN. */
export const confirmMpinError = (mpin: string, confirmation: string): string =>
  mpin === confirmation ? '' : 'The MPINs do not match.';

/** Pay-via-UPI payee field (§8.2.2): letters, digits, dot, @, underscore and
 *  hyphen only; anything else is dropped as typed. */
export const filterPayeeInput = (raw: string): string => raw.replace(/[^a-zA-Z0-9.@_-]/g, '');
